/**
 * One and Side by side, as panes — `docs/new-request.md` §10.1–§10.9,
 * Boards 7, 8, 13 B.
 *
 * The desktop aside (560 beside the form) and iPad portrait (the 620 sheet,
 * in place of the form) are the same two panes at two widths: a header, a
 * scrolling middle and — in portrait — a footer that puts the time on the
 * form. Each pane asks the server for its own view; the container owns which
 * pane is up and whether a pick is live or pending.
 */
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import {
  SIDE_BY_SIDE_MAX, clock, clockBare, clockRange, dayShort, daySummary, driveWords,
  gapsOf, lateWords, minutesWords, moveToWords, placementOf, tightWords, weekdayLong, workDaysWords,
  type CalendarPerson, type CalendarView, type DayPlan, type Opening, type Placement,
} from '@rowted/shared'
import { cn } from '../../lib/cn.js'
import { Avatar } from '../../components/primitives.js'
import {
  instantOf, timeOf, useRequestSheet, type ChosenTime, type PersonRef, type RequestFacts,
} from '../sheet-context.js'
import {
  BandButton, BestTimeCard, DashedChip, EstimatesBand, Eyebrow, Legend, MoveButton, NothingFits,
  OneSideSwitch, PersonChip, PersonSearch, ScheduleBand, SideTile,
} from './bits.js'
import { DayMap } from './day-map.js'
import { DayTimeline, axisOf, blockWords } from './day-timeline.js'
import { ChevronLeftGlyph, ChevronRightGlyph, TickGlyph } from './glyphs.js'
import { SideBySide } from './side-by-side.js'
import { Strip } from './strip.js'
import { anywayKey, useCalendar, useCompare, type Choice, type Shown } from './use-schedule.js'

export type PaneVariant = 'aside' | 'narrow'

const refOf = (person: { id: string; name: string; firstName: string }): PersonRef =>
  ({ id: person.id, name: person.name, firstName: person.firstName })

/** The people the header offers: the recommended, with whoever is shown among them. */
function useOffered(shown: PersonRef | null): PersonRef[] {
  const api = useRequestSheet()
  const recommended = (api.recommendation?.people ?? []).filter((one) => one.recommended).map(refOf)
  const out = [...recommended]
  if (shown && !out.some((one) => one.id === shown.id)) out.unshift(shown)
  return out
}

/** *near Renee Castillo Residence · 1 h 30 min + drive · …*. */
export function nearWords(facts: RequestFacts, tail: string): string {
  if (!facts.place) return `${minutesWords(facts.minutes)} + drive · add an address to see how near`
  return `near ${facts.propertyName ?? 'the property'} · ${minutesWords(facts.minutes)} + drive · ${tail}`
}

/** Esc with focus in the schedule closes it (§19 B1) — before the sheet's own Esc sees it. */
export function useEscapeCloses(root: React.RefObject<HTMLElement>, onEscape: () => void, active: boolean) {
  const latest = useRef(onEscape)
  latest.current = onEscape
  useEffect(() => {
    const node = root.current
    if (!active || !node) return undefined
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return
      event.stopPropagation()
      latest.current()
    }
    node.addEventListener('keydown', onKey)
    return () => node.removeEventListener('keydown', onKey)
  }, [root, active])
}

// ---------------------------------------------------------------------------
// The bands of a day (Board 8)
// ---------------------------------------------------------------------------

/** *One fits, at 2:45 PM (+27 min).* */
function todayCount(plan: DayPlan, minutes: number): string {
  const fits = gapsOf(plan, minutes).filter((gap) => gap.fits && gap.earliest !== null)
  if (fits.length === 0) return 'Nothing fits.'
  const first = fits[0]!
  const numbers = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six']
  if (fits.length === 1) return `One fits, at ${clock(first.earliest!)} (+${first.added} min).`
  return `${numbers[fits.length] ?? fits.length} fit, the first at ${clock(first.earliest!)} (+${first.added} min).`
}

export function TodayBand({ plan, minutes }: { plan: DayPlan; minutes: number }) {
  if (plan.notBefore === null) return null
  return (
    <ScheduleBand tone="teal">
      <b>Today:</b>
      {` openings start at ${clock(plan.notBefore)} — at least 30 min from now, on the quarter hour. `}
      {todayCount(plan, minutes)}
    </ScheduleBand>
  )
}

export function OffBand({ person, date }: { person: CalendarPerson; date: string }) {
  return (
    <ScheduleBand tone="slate">
      <b>{person.firstName} is off on {weekdayLong(date)}.</b>
      {` ${person.firstName}’s working days are ${workDaysWords(person.workDays)}, `}
      {`${clock(person.workStart)}–${clock(person.workEnd)}.`}
    </ScheduleBand>
  )
}

/**
 * Tight, late, off or outside the hours (§10.8): said plainly, with the fix
 * one click away and never a block. *Keep* and *Book it anyway* answer once
 * for that exact time.
 */
export function PlacementBand({ placement, plan, person, shown, stacked, onMove }: {
  placement: Placement
  plan: DayPlan
  person: CalendarPerson
  shown: Shown
  /** The 206 px column stacks its buttons (Board 8 A). */
  stacked?: boolean
  onMove: (start: number) => void
}) {
  const api = useRequestSheet()
  const key = anywayKey(shown)
  if (api.bookAnyway === key) return null
  const buttons = (children: ReactNode) => (
    <div className={cn('flex gap-2', stacked ? 'flex-col items-stretch gap-1.5' : 'items-center')}>{children}</div>
  )
  if (placement.state === 'tight') {
    const words = tightWords(placement)
    return (
      <div className="flex flex-col gap-2">
        <ScheduleBand tone="amber"><b>{words.title}</b> {words.body}</ScheduleBand>
        {buttons(<>
          {placement.moveTo !== null && placement.moveTo !== placement.start
            && <MoveButton label={moveToWords(placement.moveTo)} onClick={() => onMove(placement.moveTo!)} />}
          <BandButton quiet onClick={() => api.setBookAnyway(key)}>Keep {clockBare(placement.start)}</BandButton>
        </>)}
      </div>
    )
  }
  if (placement.state === 'late') {
    const words = lateWords(placement, plan, person.firstName)
    return (
      <div className="flex flex-col gap-2">
        <ScheduleBand tone="red"><b>{words.title}</b> {words.body}</ScheduleBand>
        {buttons(<>
          {placement.moveTo !== null
            && <MoveButton label={moveToWords(placement.moveTo)} onClick={() => onMove(placement.moveTo!)} />}
          <BandButton quiet onClick={() => api.setBookAnyway(key)}>Book it anyway</BandButton>
        </>)}
      </div>
    )
  }
  if (placement.state === 'off') return <OffBand person={person} date={plan.date} />
  if (placement.state === 'outside') {
    return (
      <ScheduleBand tone="slate">
        <b>{clockRange(placement.start, placement.end)} is outside {person.firstName}’s hours.</b>
        {` ${person.firstName}’s working days are ${workDaysWords(person.workDays)}, `}
        {`${clock(person.workStart)}–${clock(person.workEnd)}.`}
      </ScheduleBand>
    )
  }
  return null
}

// ---------------------------------------------------------------------------
// The summary beside the map (§10.6, T8)
// ---------------------------------------------------------------------------

function SummaryCard({ plan, placement, person }: { plan: DayPlan; placement: Placement | null; person: CalendarPerson }) {
  const api = useRequestSheet()
  const summary = daySummary(plan, placement)
  const row = (label: string, value: ReactNode, green?: boolean) => (
    <div className="mt-[7px] flex justify-between text-[12px] leading-[normal] text-text-body">
      <span>{label}</span>
      <b className={cn('font-bold', green ? 'text-accent-dark' : 'text-text')}>{value}</b>
    </div>
  )
  const about = summary.estimated ? 'about ' : ''
  const legs = plan.legs !== null
  return (
    <div className="rounded-[12px] border border-line bg-surface px-3.5 py-3 leading-[normal]">
      <b className="text-[13px] font-bold text-text">
        {weekdayLong(plan.date)}{api.facts.contactFirstName ? ` with ${api.facts.contactFirstName}` : ''}
      </b>
      {row('Appointments', summary.appointments)}
      {row('Driving', legs
        ? `${about}${minutesWords(summary.driving)}${placement ? ` (+${summary.added})` : ''}`
        : '—', Boolean(placement && legs))}
      {row('Drive before', summary.driveBefore ? driveWords(summary.driveBefore) : '—')}
      {row('Drive after', summary.driveAfter ? driveWords(summary.driveAfter) : '—')}
      {row(person.backAt, legs ? clock(summary.backAt) : '—')}
    </div>
  )
}

// ---------------------------------------------------------------------------
// One
// ---------------------------------------------------------------------------

export function OnePane({ variant, choice, footer }: {
  variant: PaneVariant
  choice: Choice
  /** iPad portrait: the footer with *Use this time*. */
  footer?: (view: CalendarView | null, placementAdded: number | null) => ReactNode
}) {
  const api = useRequestSheet()
  const schedule = api.schedule!
  const personRef = schedule.people[0]!
  const shown = choice.shown
  const wantedDay = schedule.day ?? (shown && shown.personId === personRef.id ? shown.date : null)
  const { view, loading, failed } = useCalendar(personRef.id, wantedDay, schedule.first)
  const offered = useOffered(personRef)
  const [searching, setSearching] = useState(false)
  const someoneElse = useRef<HTMLButtonElement>(null)
  const hasPlace = api.facts.place !== null
  const words = blockWords(api.facts)

  const plan = view?.day.plan ?? null
  const person = view?.person ?? null
  const chosenStart = shown && plan && person && shown.personId === person.id && shown.date === plan.date
    ? shown.start : null
  const placement = useMemo(() => (plan && chosenStart !== null
    ? placementOf(plan, chosenStart, api.facts.minutes) : null), [plan, chosenStart, api.facts.minutes])

  const timeFor = (date: string, start: number): ChosenTime =>
    ({ date, start, startsAt: instantOf(date, start, person?.timeZone ?? api.setup.timeZone) })
  const pickOpening = (opening: Opening) => {
    choice.choose(personRef, timeOf(opening))
    api.setSchedule((was) => (was ? { ...was, day: opening.date } : was))
  }
  const setDay = (date: string) => api.setSchedule((was) => (was ? { ...was, day: date } : was))
  const showPerson = (next: PersonRef) =>
    api.setSchedule((was) => (was ? { ...was, mode: 'one', people: [next], day: null, first: null } : was))
  const toSide = () => api.setSchedule((was) => {
    if (!was) return was
    const people = [personRef, ...offered.filter((one) => one.id !== personRef.id)].slice(0, SIDE_BY_SIDE_MAX)
    return { ...was, mode: 'side', people, day: plan?.date ?? was.day }
  })

  /* ‹ › move one working day; past the last, the next ten (§19 B10). */
  const stepDay = (by: 1 | -1) => {
    if (!view) return
    const working = view.strip.filter((cell) => cell.fit.kind !== 'off').map((cell) => cell.date)
    const index = working.indexOf(view.day.date)
    const next = working[index + by]
    if (next) setDay(next)
    else if (by === 1) api.setSchedule((was) => (was ? { ...was, first: view.next, day: view.next } : was))
  }

  const nothingFits = Boolean(view && hasPlace && view.best.length === 0
    && !view.strip.some((cell) => cell.fit.kind === 'fits'))
  const nextPerson = (api.recommendation?.people ?? [])
    .find((one) => one.id !== personRef.id && one.best) ?? null
  const isToday = Boolean(view && view.day.date === view.today)
  const narrow = variant === 'narrow'
  const summaryAdded = plan && placement ? daySummary(plan, placement).added : null

  const header = narrow
    ? (
      <div className="flex shrink-0 items-center gap-2.5 border-b border-line bg-surface px-[18px] py-3.5">
        <button type="button" onClick={api.closeSchedule}
          className="inline-flex h-[30px] items-center gap-1.5 rounded-button px-1.5 text-[13px] font-semibold text-accent">
          <ChevronLeftGlyph />Back to the request
        </button>
        <span className="flex-1" />
        <Avatar name={personRef.name} size={30} />
        <b className="text-[14px] font-bold text-text">{personRef.firstName}’s next two weeks</b>
        <OneSideSwitch mode="one" onChange={(mode) => { if (mode === 'side') toSide() }} />
      </div>
    )
    : (
      <div className="flex shrink-0 flex-col gap-2.5 border-b border-line bg-surface px-[18px] pb-3 pt-3.5">
        <div className="flex items-center gap-3">
          <Avatar name={personRef.name} size={36} />
          <div className="flex min-w-0 flex-1 flex-col leading-[normal]">
            <b className="text-[15px] font-bold text-text">{personRef.name}’s next two weeks</b>
            {/* Two lines when it needs them, as Board 7 draws it — never cut off. */}
            <span className="text-[12px] text-text-muted">
              {nearWords(api.facts, `from and back to ${person?.startName ?? 'the start'}`)}
            </span>
          </div>
        </div>
        <div className="relative flex items-center gap-1.5">
          {offered.map((one) => (
            <PersonChip key={one.id} person={one} selected={one.id === personRef.id} onClick={() => showPerson(one)} />
          ))}
          <DashedChip label="Someone else" chipRef={someoneElse} onClick={() => setSearching(true)} />
          <span className="flex-1" />
          <OneSideSwitch mode="one" onChange={(mode) => { if (mode === 'side') toSide() }} />
          <PersonSearch open={searching} anchor={someoneElse.current} onClose={() => setSearching(false)}
            exclude={offered.map((one) => one.id)} onPick={showPerson} />
        </div>
      </div>
    )

  const firstIsToday = Boolean(view && view.days[0] === view.today)

  return (
    <>
      {header}
      <div className={cn('flex min-h-0 flex-1 flex-col overflow-y-auto px-[18px] py-3.5',
        narrow ? 'gap-2.5' : 'gap-3', loading && 'opacity-80')} data-schedule-one>
        {!view && (
          <div className="py-6 text-center text-[12.5px] text-text-muted">
            {failed ? 'The calendar didn’t load — try again in a moment.' : 'Finding openings…'}
          </div>
        )}
        {view && person && plan && (
          <>
            {view.estimates && hasPlace && <EstimatesBand />}
            {nothingFits
              ? (
                <NothingFits
                  person={personRef}
                  minutes={minutesWords(view.minutes)}
                  next={nextPerson}
                  nextFirst={view.next}
                  onPickNext={() => {
                    if (!nextPerson?.best) return
                    const ref = refOf(nextPerson)
                    choice.choose(ref, timeOf(nextPerson.best))
                    api.setSchedule((was) => (was
                      ? { ...was, mode: 'one', people: [ref], day: nextPerson.best!.date, first: null } : was))
                  }}
                  onSideBySide={() => nextPerson && api.setSchedule((was) => (was
                    ? { ...was, mode: 'side', people: [personRef, refOf(nextPerson)], day: null } : was))}
                  onFurther={() => api.setSchedule((was) => (was ? { ...was, first: view.next, day: null } : was))}
                />
              )
              : hasPlace && view.best.length > 0 && (
                <>
                  <Eyebrow>Best times</Eyebrow>
                  <div className={cn('flex gap-2', narrow && 'flex-col')}>
                    {view.best.map((opening) => (
                      <BestTimeCard
                        key={`${opening.date}-${opening.start}`}
                        opening={opening}
                        selected={Boolean(shown && shown.personId === person.id && shown.date === opening.date
                          && shown.start === opening.start)}
                        onPick={() => pickOpening(opening)}
                      />
                    ))}
                  </div>
                </>
              )}

            {!narrow && (
              <div className="flex items-center gap-1.5">
                <Eyebrow>
                  {firstIsToday ? 'Today' : dayShort(view.days[0]!)} and the next 9 weekdays · nearest stop
                </Eyebrow>
                <span className="flex-1" />
                {!firstIsToday && (
                  <button type="button"
                    onClick={() => api.setSchedule((was) => (was ? { ...was, first: null, day: null } : was))}
                    className="inline-flex h-[22px] items-center rounded-button px-1.5 text-[12px] font-semibold text-accent">
                    <ChevronLeftGlyph size={13} />Today
                  </button>
                )}
                <button type="button"
                  onClick={() => api.setSchedule((was) => (was ? { ...was, first: view.next, day: null } : was))}
                  className="inline-flex h-[22px] items-center gap-1 rounded-button px-1.5 text-[12px] font-semibold text-accent">
                  {dayShort(view.next)}<ChevronRightGlyph />
                </button>
              </div>
            )}
            <Strip cells={view.strip} today={view.today} selected={view.day.date} hasPlace={hasPlace}
              onPick={setDay} width={narrow ? 584 : 524} />

            {plan.off && <OffBand person={person} date={plan.date} />}
            {isToday && hasPlace && !plan.off && <TodayBand plan={plan} minutes={view.minutes} />}

            <div className={cn('mt-1 flex', narrow ? 'gap-3' : 'gap-2.5')}>
              <div className={cn('flex flex-none flex-col', narrow ? 'w-[330px]' : 'w-[308px]')}>
                {!narrow && (
                  <div className="mb-2.5 flex w-[308px] items-center gap-2">
                    <b className="text-[13px] font-bold text-text">{dayShort(plan.date)}</b>
                    {chosenStart !== null && <span className="text-[12px] text-text-muted">drag the green block</span>}
                    <span className="flex-1" />
                    <button type="button" aria-label="The working day before" onClick={() => stepDay(-1)}
                      className="inline-flex h-[26px] w-[26px] items-center justify-center rounded-button border border-line bg-surface text-text">
                      <ChevronLeftGlyph />
                    </button>
                    <button type="button" aria-label="The next working day" onClick={() => stepDay(1)}
                      className="inline-flex h-[26px] w-[26px] items-center justify-center rounded-button border border-line bg-surface text-text">
                      <ChevronRightGlyph size={15} />
                    </button>
                  </div>
                )}
                <DayTimeline
                  plan={plan}
                  person={person}
                  minutes={view.minutes}
                  chosenStart={chosenStart}
                  axis={axisOf([plan])}
                  words={words}
                  width={262}
                  onPick={(start) => choice.choose(personRef, timeFor(plan.date, start))}
                  onMove={(start) => choice.move(personRef, timeFor(plan.date, start))}
                />
              </div>
              <div className={cn('flex min-w-0 flex-none flex-col', narrow ? 'w-[232px]' : 'w-[206px]')}>
                {placement && shown && placement.state !== 'ok' && (
                  <div className="mb-2.5">
                    <PlacementBand placement={placement} plan={plan} person={person} shown={shown} stacked
                      onMove={(start) => choice.move(personRef, timeFor(plan.date, start))} />
                  </div>
                )}
                <DayMap day={view.day} plan={plan} gap={placement?.gap ?? null}
                  width={narrow ? 232 : 206} height={narrow ? 232 : 236} />
                <div className="h-2.5" />
                {!narrow && (
                  <>
                    <SummaryCard plan={plan} placement={placement} person={person} />
                    <div className="h-2.5" />
                  </>
                )}
                <Legend />
              </div>
            </div>
          </>
        )}
      </div>
      {footer?.(view, summaryAdded)}
    </>
  )
}

// ---------------------------------------------------------------------------
// Side by side
// ---------------------------------------------------------------------------

export function SidePane({ variant, choice, footer }: {
  variant: PaneVariant
  choice: Choice
  footer?: ReactNode
}) {
  const api = useRequestSheet()
  const schedule = api.schedule!
  const shown = choice.shown
  const ids = schedule.people.map((one) => one.id)
  const wantedDate = schedule.day ?? (shown && ids.includes(shown.personId) ? shown.date : null)
  const { view, loading, failed } = useCompare(ids, wantedDate, schedule.first)
  const [searching, setSearching] = useState(false)
  const addSomeone = useRef<HTMLButtonElement>(null)
  const narrow = variant === 'narrow'

  const remove = (id: string) => {
    if (schedule.people.length <= 1) { api.closeSchedule(); return }
    api.setSchedule((was) => (was ? { ...was, people: was.people.filter((one) => one.id !== id) } : was))
  }
  const add = (person: PersonRef) => api.setSchedule((was) => (was && was.people.length < SIDE_BY_SIDE_MAX
    && !was.people.some((one) => one.id === person.id) ? { ...was, people: [...was.people, person] } : was))
  const toOne = () => api.setSchedule((was) => {
    if (!was) return was
    const chosen = was.people.find((one) => one.id === shown?.personId) ?? was.people[0]!
    return { ...was, mode: 'one', people: [chosen], day: null }
  })

  return (
    <>
      <div className={cn('flex shrink-0 flex-col gap-2.5 border-b border-line bg-surface px-[18px] pb-3 pt-3.5')}>
        <div className="flex items-center gap-3">
          {narrow && (
            <button type="button" onClick={api.closeSchedule}
              className="inline-flex h-[30px] items-center gap-1.5 rounded-button px-1.5 text-[13px] font-semibold text-accent">
              <ChevronLeftGlyph />Back to the request
            </button>
          )}
          <SideTile />
          <div className="flex min-w-0 flex-1 flex-col leading-[normal]">
            <b className="text-[15px] font-bold text-text">
              Side by side{view ? ` · ${dayShort(view.date)}` : ''}
            </b>
            <span className="truncate text-[12px] text-text-muted">{nearWords(api.facts, 'each from their own start')}</span>
          </div>
        </div>
        <div className="relative flex items-center gap-1.5">
          {schedule.people.map((one) => (
            <PersonChip key={one.id} person={one} selected onRemove={() => remove(one.id)} />
          ))}
          {schedule.people.length < SIDE_BY_SIDE_MAX && (
            <DashedChip label="Add someone" chipRef={addSomeone} onClick={() => setSearching(true)} />
          )}
          <span className="flex-1" />
          <OneSideSwitch mode="side" onChange={(mode) => { if (mode === 'one') toOne() }} />
          <PersonSearch open={searching} anchor={addSomeone.current} onClose={() => setSearching(false)}
            exclude={ids} onPick={add} action="Add" />
        </div>
      </div>
      <div className={cn('flex min-h-0 flex-1 flex-col gap-2.5 overflow-y-auto px-[18px] py-3.5',
        loading && 'opacity-80')} data-schedule-side>
        {!view && (
          <div className="py-6 text-center text-[12.5px] text-text-muted">
            {failed ? 'The calendars didn’t load — try again in a moment.' : 'Finding openings…'}
          </div>
        )}
        {view && (
          <>
            {view.estimates && api.facts.place && <EstimatesBand />}
            <SideBySide
              view={view}
              shown={shown}
              words={blockWords(api.facts)}
              divisionName={api.facts.divisionName}
              segment={api.facts.segment}
              hasPlace={api.facts.place !== null}
              onDay={(date) => api.setSchedule((was) => (was ? { ...was, day: date } : was))}
              onPick={(person, opening) => {
                choice.choose(refOf(person), {
                  date: opening.date, start: opening.start,
                  startsAt: opening.startsAt ?? instantOf(opening.date, opening.start, person.timeZone),
                })
                api.setSchedule((was) => (was ? { ...was, day: opening.date } : was))
              }}
            />
          </>
        )}
      </div>
      {footer}
    </>
  )
}

/** *Thu Oct 22 · 10:00–11:30 AM · +5 min drive* and **Use this time** (Board 13 B). */
export function UseThisTime({ words, onUse }: { words: string; onUse: () => void }) {
  return (
    <div className="mt-auto flex shrink-0 items-center gap-2.5 border-t border-line bg-surface px-[18px] py-3.5">
      <span className="min-w-0 flex-1 truncate text-[12.5px] text-text-muted">{words}</span>
      <button type="button" onClick={onUse}
        className="inline-flex h-10 items-center gap-1.5 whitespace-nowrap rounded-button border border-accent bg-accent px-3.5 text-[13.5px] font-semibold text-surface hover:bg-accent-dark">
        <TickGlyph size={15} />Use this time
      </button>
    </div>
  )
}

/** The footer's words for a time. */
export function timeWords(date: string, start: number, minutes: number, added: number | null): string {
  return `${dayShort(date)} · ${clockRange(start, start + minutes)}${added !== null ? ` · +${added} min drive` : ''}`
}
